/**
 * Pontos de contato BLACKFEO (faixa, destaque, CTA de artigo e pop-up), todos
 * levando ao catálogo. Antes da virada só aparecem com a prévia (?preview=1).
 */
import { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import logoBlue from "@/assets/blackfeo/logo-1.webp.asset.json";
import { proximoResetBlackfeo } from "@/lib/black-november/blackfeo-contador";
import { BLACKFEO_CATALOGO, comPreviewAntesDaVirada } from "@/lib/black-november/ofertas-lps";

export const BLACKFEO_CHAMADA = "Todos os nossos produtos com até 80% de desconto";

/** Link ao catálogo com ponto de contato + UTMs/trk da visita (aplicado no clique). */
export function blackfeoHref(ponto: string): string {
  const base = comPreviewAntesDaVirada(`${BLACKFEO_CATALOGO}?ponto=${encodeURIComponent(ponto)}`, Date.now());
  if (typeof window === "undefined") return base;
  const u = new URL(base, window.location.origin);
  new URLSearchParams(window.location.search).forEach((v, k) => {
    if ((k.startsWith("utm_") || k === "trk" || k === "tracker_trk") && !u.searchParams.has(k)) u.searchParams.set(k, v);
  });
  return u.pathname + u.search;
}
function linkCatalogo(ponto: string) {
  const set = (e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.href = blackfeoHref(ponto); };
  return { href: `${BLACKFEO_CATALOGO}?ponto=${encodeURIComponent(ponto)}`, onMouseDown: set, onClick: set };
}

function useRestante() {
  const [s, setS] = useState<number | null>(null);
  useEffect(() => {
    const tick = () => { const n = Date.now(); setS(Math.max(0, Math.floor((proximoResetBlackfeo(n) - n) / 1000))); };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return s;
}
function ContadorCurto() {
  const t = useRestante() ?? 0;
  const p = (n: number) => String(n).padStart(2, "0");
  const d = Math.floor(t / 86400);
  return <span className="font-mono tabular-nums">{d > 0 ? `${d}d ` : ""}{p(Math.floor((t % 86400) / 3600))}:{p(Math.floor((t % 3600) / 60))}:{p(t % 60)}</span>;
}

/** Faixa no topo das páginas de produto. */
export function BlackfeoTopBar({ ponto, off }: { ponto: string; off?: string }) {
  return (
    <div className="blackfeo-scope border-b border-border">
      <a {...linkCatalogo(ponto)} className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-center text-xs font-bold sm:text-sm">
        <img src={logoBlue.url} alt="BLACKFEO" width={863} height={507} decoding="async" className="h-7 w-auto sm:h-8" />
        <span>{off ? <><span className="text-black-campaign-lilac">{off}</span> nesta página · </> : null}até 80% OFF em todo o catálogo</span>
        <span className="inline-flex items-center gap-1.5 text-black-campaign-cyan">Termina em <ContadorCurto /></span>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[0.7rem] uppercase text-primary-foreground">Ver catálogo <ArrowRight className="h-3 w-3" aria-hidden /></span>
      </a>
    </div>
  );
}

/** Bloco de destaque (home, institucionais e artigos). */
export function BlackfeoDestaque({ ponto, compacto = false }: { ponto: string; compacto?: boolean }) {
  return (
    <section aria-label="BLACKFEO" className={`blackfeo-scope relative isolate overflow-hidden text-center ${compacto ? "rounded-2xl px-6 py-8 sm:py-10" : "px-5 py-12 sm:py-16"}`}>
      <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/30 blur-[120px]" />
      <div aria-hidden className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-accent/25 blur-[120px]" />
      <div className="relative mx-auto max-w-3xl">
        <img src={logoBlue.url} alt="BLACKFEO" width={863} height={507} loading="lazy" decoding="async" className={`mx-auto h-auto ${compacto ? "w-44 sm:w-56" : "w-52 sm:w-64"}`} />
        <p className={`mt-5 font-black leading-tight ${compacto ? "text-xl sm:text-2xl" : "text-2xl sm:text-4xl"}`}>
          Todos os nossos produtos com <span className="text-black-campaign-lilac">até 80% de desconto</span>
        </p>
        <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground sm:text-base">Pós-graduações, certificações, STAR Cursos e Agente IA com os menores preços do ano.</p>
        <p className="mt-4 text-xs font-extrabold uppercase tracking-wider text-black-campaign-cyan sm:text-sm">Condições especiais terminam em <ContadorCurto /></p>
        <a {...linkCatalogo(ponto)} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-black uppercase tracking-wide text-primary-foreground no-underline hover:brightness-110">
          Ver ofertas <ArrowRight className="h-4 w-4" aria-hidden />
        </a>
      </div>
    </section>
  );
}

/** Pop-up (uma vez por sessão), após 50% de rolagem ou intenção de saída. */
export function BlackfeoPopup({ ponto }: { ponto: string }) {
  const [aberto, setAberto] = useState(false);
  useEffect(() => {
    const KEY = "feo-blackfeo-popup";
    try { if (sessionStorage.getItem(KEY)) return; } catch { /* sem storage */ }
    let feito = false;
    const mostrar = () => {
      if (feito) return; feito = true;
      try { sessionStorage.setItem(KEY, "1"); } catch { /* sem storage */ }
      setAberto(true); limpar();
    };
    const onScroll = () => { const h = document.documentElement; if ((h.scrollTop + window.innerHeight) / h.scrollHeight > 0.5) mostrar(); };
    const onSaida = (e: MouseEvent) => { if (!e.relatedTarget && e.clientY <= 0) mostrar(); };
    const limpar = () => { window.removeEventListener("scroll", onScroll); document.removeEventListener("mouseout", onSaida); };
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseout", onSaida);
    return limpar;
  }, []);
  useEffect(() => {
    if (!aberto) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setAberto(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aberto]);
  if (!aberto) return null;
  return (
    <div role="dialog" aria-modal="true" aria-label="BLACKFEO" className="blackfeo-scope fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && setAberto(false)}>
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card px-6 py-9 text-center shadow-2xl">
        <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/30 blur-[100px]" />
        <button type="button" aria-label="Fechar" onClick={() => setAberto(false)} className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-5 w-5" /></button>
        <div className="relative">
          <img src={logoBlue.url} alt="BLACKFEO" width={863} height={507} decoding="async" className="mx-auto h-auto w-52" />
          <p className="mt-5 text-xl font-black leading-tight">Todos os nossos produtos com <span className="text-black-campaign-lilac">até 80% de desconto</span></p>
          <p className="mt-3 text-sm text-muted-foreground">Pós-graduações, certificações, STAR e Agente IA.</p>
          <p className="mt-4 text-xs font-extrabold uppercase tracking-wider text-black-campaign-cyan">Termina em <ContadorCurto /></p>
          <a {...linkCatalogo(`${ponto}-popup`)} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3 text-sm font-black uppercase tracking-wide text-primary-foreground hover:brightness-110">
            Ver ofertas <ArrowRight className="h-4 w-4" aria-hidden />
          </a>
        </div>
      </div>
    </div>
  );
}
